import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { resolvePublicAssetUrl } from '../utils/resolvePublicAssetUrl';
import { countryToCellMap, getCountryFromCellId } from './countryToCellMapping';
import { normalizeCountryName } from './countryCodeMapping';
import CountryPath from './CountryPath';
import { preloadWarEffortMapPaintImages } from './mapPaintImages';
import './WorldMap.css';

/**
 * WorldMap Component
 * Displays an interactive SVG world map where all countries are clickable
 * 
 * Props:
 * - cellOwnersMap: Map of cellId -> faction name
 * - beaconCells: Array of cell IDs with waiting players
 * - onCountryClick: Callback when a country is clicked (receives cellId)
 * - onCountryHover: Callback when a country is hovered (receives cellId, event)
 * - onCountryLeave: Callback when mouse leaves a country
 * - getFactionBackground: Function returning a CSS color string, or `{ color, image }` where
 *   `image` is a public path (e.g. `/images/red_paint.jpg`) used as an SVG pattern fill for
 *   non-fortified countries. Fortified countries keep the stripe pattern using `color`.
 * - deploying: Boolean indicating if deployment is in progress
 * - highlightedCells: Array of cell IDs to highlight (for mission system)
 * - fortifiedCells: Array of cell IDs that are fortified (for mission system)
 * - opponentFortifiedCells: Array of cell IDs fortified by the opponent via fortify-country action; faction stripe
 * - opponentCommandCenterFortifiedCells: Array of cell IDs fortified by opponent Command Center (own 3+ neighbours); distinct stripe
 * - opponentCommandCenterStripeBaseColor: Optional hex for opponent Command Center stripe (opponent purpose base color)
 * - opponentCommandCenterStripeDarkerColor: Optional hex for opponent Command Center stripe (opponent purpose darker color)
 * - commandCenterFortifiedCells: Array of cell IDs fortified by Command Center (own 3+ neighbours)
 * - viewerCommandCenterStripeBaseColor: Optional hex for Command Center stripe (purpose base color)
 * - viewerCommandCenterStripeDarkerColor: Optional hex for Command Center stripe (purpose darker color); if only base set, darker is derived
 * - selectedCell: Cell ID that is currently selected (for mission system)
 * - missionTargetCell: Cell ID of the mission target (for mission system)
 * - attackArrowFromCellId: Origin cell for the attack-mode arrow; destination follows the hovered country via DOM (no React hover state)
 * - isAttackArrowTargetCell: Optional (cellId) => boolean; when false the hover arrow is hidden
 * - devModeHighlightCells: Array of cell IDs to highlight in dev mode (golden border for command center eligible)
 * - missionSubmissionHoverCellId: Cell ID of the mission submission row being hovered (e.g. "Conquer Seychelles") to highlight that country on the map
 * - neutralFortificationCountMap: Map of cellId -> 1|2|3 for neutral countries with random fortifications; painted with darkblue / pink / golden paint.
 * - hintPopup: { cellId, message } to show a brief popup with arrow pointing at the cell (e.g. Madagascar trade hint)
 * - factionsInGame: Optional array or Set of faction names that currently have a player. Countries owned by factions
 *   not in this set are shown with gray_paint.jpg (ownership unchanged, visualization only). Omit or null = show all factions in their paint/colors.
 * - cellsUnderAttack: Optional array of cell IDs that currently have an attack in progress (entire country pulsates red; original color shows through when pulse ebbs).
 * - newTradeOfferCells: Optional array of cell IDs with unseen Trading Hub offers (Madagascar pulsates gold until the player opens the hub).
 */
const DARK_GRAY_NOT_IN_GAME = '#2a2e35'; // Unused-faction color when a paint image is not applied
const UNUSED_FACTION_PAINT_IMAGE = '/images/gray_paint.jpg';
const ACTION_FORTIFY_STRIPE_COLOR = '#6a6a6a';
const ACTION_FORTIFY_STRIPE_PATTERN_ID = 'stripe-pattern-action-fortify';
const NEUTRAL_FORT_PAINT_IMAGES = {
  1: '/images/darkblue_plaint.jpg',
  2: '/images/pink_paint.jpg',
  3: '/images/golden_paint.jpg'
};

// Stable pattern id for per-cell opponent Command Center stripes (FFA/2v2)
function opponentCcPatternIdFromColors(base, darker) {
  return `stripe-pattern-opponent-cc-${(base || '').replace(/^#/, '')}-${(darker || '').replace(/^#/, '')}`;
}

function factionImagePatternId(imagePath) {
  if (!imagePath || typeof imagePath !== 'string') {
    throw new Error('factionImagePatternId: imagePath is required');
  }
  const fileName = imagePath.split('/').pop();
  if (!fileName) {
    throw new Error(`factionImagePatternId: could not parse file name from "${imagePath}"`);
  }
  const baseName = fileName.replace(/\.[^.]+$/, '');
  const sanitized = baseName.replace(/[^a-zA-Z0-9_-]/g, '-');
  if (!sanitized) {
    throw new Error(`factionImagePatternId: empty pattern id from "${imagePath}"`);
  }
  return `faction-image-${sanitized}`;
}

const UNUSED_FACTION_PAINT_PATTERN_ID = factionImagePatternId(UNUSED_FACTION_PAINT_IMAGE);

function neutralFortPaintPatternId(count) {
  const image = NEUTRAL_FORT_PAINT_IMAGES[count];
  if (!image) {
    throw new Error(`neutralFortPaintPatternId: fortification count must be 1, 2, or 3 (got ${count})`);
  }
  return factionImagePatternId(image);
}

function createFactionImagePattern(patternId, imagePath) {
  if (!patternId) {
    throw new Error('createFactionImagePattern: patternId is required');
  }
  if (!imagePath) {
    throw new Error('createFactionImagePattern: imagePath is required');
  }
  return (
    <pattern
      key={patternId}
      id={patternId}
      patternUnits="objectBoundingBox"
      patternContentUnits="objectBoundingBox"
      width="1"
      height="1"
    >
      <image
        href={resolvePublicAssetUrl(imagePath)}
        x="0"
        y="0"
        width="1"
        height="1"
        preserveAspectRatio="xMidYMid slice"
      />
    </pattern>
  );
}

function createDarkerStripeOverlayPattern(patternId, darkerColor) {
  if (!patternId) {
    throw new Error('createDarkerStripeOverlayPattern: patternId is required');
  }
  if (!darkerColor) {
    throw new Error('createDarkerStripeOverlayPattern: darkerColor is required');
  }
  return (
    <pattern
      key={patternId}
      id={patternId}
      patternUnits="userSpaceOnUse"
      width="12"
      height="12"
      patternTransform="rotate(45)"
    >
      <rect x="6" width="6" height="12" fill={darkerColor} />
    </pattern>
  );
}

const UNUSED_FACTION_PAINT_PATTERN = createFactionImagePattern(UNUSED_FACTION_PAINT_PATTERN_ID, UNUSED_FACTION_PAINT_IMAGE);
const ACTION_FORTIFY_STRIPE_PATTERN = createDarkerStripeOverlayPattern(ACTION_FORTIFY_STRIPE_PATTERN_ID, ACTION_FORTIFY_STRIPE_COLOR);
const NEUTRAL_FORT_PAINT_PATTERNS = [1, 2, 3].map((count) => (
  createFactionImagePattern(neutralFortPaintPatternId(count), NEUTRAL_FORT_PAINT_IMAGES[count])
));
const SVG_NS = 'http://www.w3.org/2000/svg';

let cachedWorldMapPaths = null;
let cachedWorldMapViewBox = null;

function parseFactionBackgroundResult(bgResult, factionName) {
  if (bgResult == null || bgResult === '') {
    throw new Error(`WorldMap: getFactionBackground returned no fill for faction "${factionName}"`);
  }
  if (typeof bgResult === 'object') {
    if (!bgResult.color) {
      throw new Error(`WorldMap: getFactionBackground("${factionName}") object result requires color`);
    }
    if (Object.prototype.hasOwnProperty.call(bgResult, 'image')) {
      if (!bgResult.image || typeof bgResult.image !== 'string') {
        throw new Error(`WorldMap: getFactionBackground("${factionName}") image must be a non-empty string`);
      }
      return { color: bgResult.color, image: bgResult.image };
    }
    return { color: bgResult.color, image: null };
  }
  return { color: bgResult, image: null };
}

const WorldMap = ({
  cellOwnersMap = {},
  beaconCells = [],
  onCountryClick,
  onCountryHover,
  onCountryLeave,
  getFactionBackground,
  deploying = false,
  interactive = true,
  highlightedCells = [],
  fortifiedCells = [],
  commandCenterFortifiedCells = [],
  viewerCommandCenterStripeBaseColor = null,
  viewerCommandCenterStripeDarkerColor = null,
  opponentFortifiedCells = [],
  opponentCommandCenterFortifiedCells = [],
  opponentCommandCenterStripeBaseColor = null,
  opponentCommandCenterStripeDarkerColor = null,
  opponentCommandCenterStripeByCellId = {},
  selectedCell = null,
  missionTargetCell = null,
  missionSubmissionHoverCellId = null,
  attackArrowFromCellId = null,
  isAttackArrowTargetCell = null,
  devModeHighlightCells = [],
  neutralFortificationCountMap = {},
  hintPopup = null,
  factionsInGame = null,
  cellsUnderAttack = [],
  newTradeOfferCells = [],
  tectonicBoredriveCells = [],
  resetViewTrigger = 0
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const hoveredCellIdRef = useRef(null);
  const hoverLeaveTimerRef = useRef(null);
  const hoverOutlineGroupRef = useRef(null);
  const cellCenterByIdRef = useRef(new Map());
  const hoverGeometriesRef = useRef(new Map());
  const arrowGroupRef = useRef(null);
  const arrowShaftRef = useRef(null);
  const arrowGlowRef = useRef(null);
  const arrowHeadRef = useRef(null);
  const attackArrowFromCellIdRef = useRef(attackArrowFromCellId);
  const isAttackArrowTargetCellRef = useRef(isAttackArrowTargetCell);
  attackArrowFromCellIdRef.current = attackArrowFromCellId;
  isAttackArrowTargetCellRef.current = isAttackArrowTargetCell;
  const [svgPaths, setSvgPaths] = useState([]);
  const [svgViewBox, setSvgViewBox] = useState('0 0 1000 500');
  const [hintTargetCoords, setHintTargetCoords] = useState(null);
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [panStart, setPanStart] = useState(null);

  // Refs for latest pan/zoom so wheel handler stays stable and never has stale closure
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  panRef.current = pan;
  zoomRef.current = zoom;
  const touchDragStartRef = useRef(null);
  const touchPanStartRef = useRef(null);

  const hoverGeometriesByCellId = React.useMemo(() => {
    const map = new Map();
    svgPaths.forEach((p) => {
      if (p.cellId == null) return;
      const key = String(p.cellId);
      const list = map.get(key);
      if (list) {
        list.push(p);
      } else {
        map.set(key, [p]);
      }
    });
    return map;
  }, [svgPaths]);
  hoverGeometriesRef.current = hoverGeometriesByCellId;

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg || svgPaths.length === 0) return;
    const centers = new Map();
    svg.querySelectorAll('[data-mapped="true"][data-cell-id]').forEach((el) => {
      const id = el.getAttribute('data-cell-id');
      if (!id || centers.has(id)) return;
      try {
        const b = el.getBBox();
        centers.set(id, { x: b.x + b.width / 2, y: b.y + b.height / 2 });
      } catch {
        // Invalid geometry for this fragment; skip.
      }
    });
    cellCenterByIdRef.current = centers;
  }, [svgPaths.length]);

  const syncAttackArrow = React.useCallback((toCellId) => {
    const group = arrowGroupRef.current;
    const shaft = arrowShaftRef.current;
    const glow = arrowGlowRef.current;
    const head = arrowHeadRef.current;
    if (!group || !shaft || !glow || !head) return;

    const hide = () => {
      group.setAttribute('visibility', 'hidden');
    };

    const fromId = attackArrowFromCellIdRef.current;
    const isTarget = isAttackArrowTargetCellRef.current;
    if (fromId == null || toCellId == null || Number(fromId) === Number(toCellId)) {
      hide();
      return;
    }
    if (typeof isTarget === 'function' && !isTarget(toCellId)) {
      hide();
      return;
    }

    const fromCenter = cellCenterByIdRef.current.get(String(fromId));
    const toCenter = cellCenterByIdRef.current.get(String(toCellId));
    if (!fromCenter || !toCenter) {
      hide();
      return;
    }
    const x1 = fromCenter.x;
    const y1 = fromCenter.y;
    const x2 = toCenter.x;
    const y2 = toCenter.y;
    glow.setAttribute('x1', String(x1));
    glow.setAttribute('y1', String(y1));
    glow.setAttribute('x2', String(x2));
    glow.setAttribute('y2', String(y2));
    shaft.setAttribute('x1', String(x1));
    shaft.setAttribute('y1', String(y1));
    shaft.setAttribute('x2', String(x2));
    shaft.setAttribute('y2', String(y2));
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const headLen = 12;
    const ax = x2 - ux * headLen;
    const ay = y2 - uy * headLen;
    const perp = 8;
    const p1x = ax + uy * perp;
    const p1y = ay - ux * perp;
    const p2x = ax - uy * perp;
    const p2y = ay + ux * perp;
    head.setAttribute('points', `${x2},${y2} ${p1x},${p1y} ${p2x},${p2y}`);
    group.setAttribute('visibility', 'visible');
  }, []);

  useLayoutEffect(() => {
    syncAttackArrow(hoveredCellIdRef.current);
  }, [attackArrowFromCellId, svgPaths.length, syncAttackArrow]);

  // Compute hint popup target (cell center) when hintPopup is set
  useEffect(() => {
    if (!hintPopup || hintPopup.cellId == null) {
      setHintTargetCoords(null);
      return;
    }
    const rafId = requestAnimationFrame(() => {
      if (!svgRef.current) return;
      const el = svgRef.current.querySelector(`[data-cell-id="${hintPopup.cellId}"]`);
      if (!el) {
        setHintTargetCoords(null);
        return;
      }
      try {
        const b = el.getBBox();
        setHintTargetCoords({
          cx: b.x + b.width / 2,
          cy: b.y + b.height / 2
        });
      } catch (e) {
        setHintTargetCoords(null);
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [hintPopup, svgPaths.length]);

  // Load and parse world map SVG (reuse session cache so returning from HQ does not re-fetch)
  useEffect(() => {
    preloadWarEffortMapPaintImages();
    if (cachedWorldMapPaths && cachedWorldMapViewBox) {
      setSvgPaths(cachedWorldMapPaths);
      setSvgViewBox(cachedWorldMapViewBox);
      return undefined;
    }
    const loadWorldMap = async () => {
      try {
        const response = await fetch('/images/world-map.svg');
        if (response.ok) {
          const svgText = await response.text();
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
          
          // Get viewBox from SVG
          const svgElement = svgDoc.documentElement;
          const viewBox = svgElement.getAttribute('viewBox') || svgElement.getAttribute('viewbox') || '0 0 1000 500';
          setSvgViewBox(viewBox);
          cachedWorldMapViewBox = viewBox;
          
          // Extract all paths, polygons, and circles
          const paths = svgDoc.querySelectorAll('path, polygon, circle');
          const pathData = Array.from(paths).map((path, index) => {
            // Try multiple ways to get country identifier
            // Priority: name attribute (full country name) > id (country code) > class > title
            const identifier = path.getAttribute('name') || 
                              path.id || 
                              path.getAttribute('class') ||
                              path.getAttribute('title') || 
                              path.getAttribute('data-name') ||
                              path.getAttribute('data-country') ||
                              '';
            
            // Store original identifier for debugging
            const originalIdentifier = identifier;
            
            // Normalize to full country name (handles codes, classes, etc.)
            const countryName = normalizeCountryName(identifier);
            
            const cellId = countryName ? countryToCellMap[countryName] : null;
            
            return {
              id: path.id || `path-${index}`,
              type: path.tagName.toLowerCase(),
              d: path.getAttribute('d') || '',
              points: path.getAttribute('points') || '',
              cx: path.getAttribute('cx') || '',
              cy: path.getAttribute('cy') || '',
              r: path.getAttribute('r') || '',
              countryName,
              cellId,
              originalIdentifier, // Store for debugging
              originalFill: path.getAttribute('fill') || 'rgba(0, 0, 0, 0.1)',
              originalStroke: path.getAttribute('stroke') || 'rgba(255, 255, 255, 0.1)',
              originalStrokeWidth: path.getAttribute('stroke-width') || '1'
            };
          });
          
          setSvgPaths(pathData);
          cachedWorldMapPaths = pathData;
          console.log(`✅ Loaded ${pathData.length} paths from world map`);
          const mappedCount = pathData.filter(p => p.cellId).length;
          console.log(`✅ ${mappedCount} countries mapped to cell IDs`);
          
          // Debug: Show ALL unmapped countries with their exact SVG identifiers
          const unmappedCountries = pathData.filter(p => !p.cellId && p.countryName);
          
          if (unmappedCountries.length > 0) {
            // Only log summary - full list is too verbose
            console.log(`⚠️ ${unmappedCountries.length} unmapped countries (mostly islands/territories). These appear gray on the map.`);
          } else {
            console.log('✅ All countries are mapped!');
          }
          
          // Removed verbose debug logs for specific countries
        } else {
          console.warn('⚠️ World map SVG not found at /images/world-map.svg');
          console.warn('Please add a world-map.svg file with country paths');
        }
      } catch (error) {
        console.error('Error loading world map:', error);
      }
    };

    loadWorldMap();
  }, []);

  const applyHoveredCountryOutline = React.useCallback((cellId) => {
    const prevId = hoveredCellIdRef.current;
    if (prevId === cellId) return;
    const layer = hoverOutlineGroupRef.current;
    if (layer) {
      while (layer.firstChild) {
        layer.removeChild(layer.firstChild);
      }
    }
    if (layer && cellId != null) {
      const geoms = hoverGeometriesRef.current.get(String(cellId));
      if (geoms) {
        geoms.forEach((p) => {
          let el;
          if (p.type === 'path') {
            el = document.createElementNS(SVG_NS, 'path');
            if (p.d) el.setAttribute('d', p.d);
          } else if (p.type === 'polygon') {
            el = document.createElementNS(SVG_NS, 'polygon');
            if (p.points) el.setAttribute('points', p.points);
          } else if (p.type === 'circle') {
            el = document.createElementNS(SVG_NS, 'circle');
            if (p.cx) el.setAttribute('cx', p.cx);
            if (p.cy) el.setAttribute('cy', p.cy);
            if (p.r) el.setAttribute('r', p.r);
          } else {
            return;
          }
          el.setAttribute('fill', 'none');
          el.setAttribute('stroke', 'rgba(255, 255, 255, 1)');
          el.setAttribute('stroke-width', '2.4');
          el.setAttribute('pointer-events', 'none');
          layer.appendChild(el);
        });
      }
    }
    hoveredCellIdRef.current = cellId;
  }, []);

  const clearHoveredCountryOutline = React.useCallback(() => {
    applyHoveredCountryOutline(null);
  }, [applyHoveredCountryOutline]);

  const handleCountryClick = React.useCallback((cellId, e) => {
    // Prevent map dragging if clicking on a country
    if (e) {
      e.stopPropagation();
    }
    // Only trigger click if we didn't drag
    if (cellId && onCountryClick && !deploying && !hasDragged) {
      onCountryClick(cellId);
    }
  }, [onCountryClick, deploying, hasDragged]);

  const handleCountryMouseEnter = React.useCallback((e, cellId) => {
    if (!cellId || deploying) return;
    if (hoverLeaveTimerRef.current != null) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
    applyHoveredCountryOutline(cellId);
    syncAttackArrow(cellId);
    if (onCountryHover) {
      onCountryHover(cellId, e);
    }
  }, [onCountryHover, deploying, applyHoveredCountryOutline, syncAttackArrow]);

  const handleCountryMouseLeave = React.useCallback(() => {
    if (hoverLeaveTimerRef.current != null) {
      clearTimeout(hoverLeaveTimerRef.current);
    }
    // Crossing a country border fires leave then enter. Delay the clear so the
    // highlight/tooltip do not drop to empty between neighbours (visible flicker).
    hoverLeaveTimerRef.current = setTimeout(() => {
      hoverLeaveTimerRef.current = null;
      clearHoveredCountryOutline();
      syncAttackArrow(null);
      if (onCountryLeave) {
        onCountryLeave();
      }
    }, 60);
  }, [onCountryLeave, clearHoveredCountryOutline, syncAttackArrow]);

  useEffect(() => {
    return () => {
      if (hoverLeaveTimerRef.current != null) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
    };
  }, []);

  // Handle zoom with mouse wheel. Stable callback (no deps) so listener is registered once.
  const handleWheel = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const prevZoom = zoomRef.current;
    const prevPan = panRef.current;
    const newZoom = Math.max(0.5, Math.min(5, prevZoom * zoomFactor));
    const svgX = (mouseX - prevPan.x) / prevZoom;
    const svgY = (mouseY - prevPan.y) / prevZoom;
    const newPanX = mouseX - svgX * newZoom;
    const newPanY = mouseY - svgY * newZoom;
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }, []);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    const isCountry = e.target.tagName === 'path' || e.target.tagName === 'polygon' || e.target.tagName === 'circle';
    
    // Start dragging for:
    // - Middle mouse button (button 1) - works everywhere including countries
    // - Right mouse button (button 2) - works everywhere including countries
    // - Left mouse with Ctrl/Cmd/Shift key - works everywhere including countries
    // - Left mouse on empty space (svg or g element)
    // - Left mouse on countries ONLY if moved enough (handled in mousemove)
    if (e.button === 1 || e.button === 2 || e.ctrlKey || e.metaKey || e.shiftKey || 
        e.target.tagName === 'svg' || e.target.tagName === 'g') {
      if (e.button === 1 || e.button === 2) {
        e.preventDefault();
      }
      setIsDragging(true);
      setHasDragged(false);
      setDragStart({ x: e.clientX, y: e.clientY });
      setPanStart({ x: pan.x, y: pan.y });
    } else if (isCountry && e.button === 0) {
      // Left click on country - prepare for potential drag but don't start dragging yet
      // Only start dragging if mouse moves more than threshold
      setDragStart({ x: e.clientX, y: e.clientY });
      setPanStart({ x: pan.x, y: pan.y });
      // Don't set isDragging yet - let the click work normally
      return; // Don't prevent default - allow click to work
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      // Mark as dragged if moved more than 3 pixels
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        setHasDragged(true);
      }
      setPan({
        x: panStart.x + deltaX,
        y: panStart.y + deltaY
      });
    } else if (dragStart !== null && panStart !== null) {
      // Check if we should start dragging from a country click
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      // If moved more than 3 pixels, start dragging
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        setIsDragging(true);
        setHasDragged(true);
        setPan({
          x: panStart.x + deltaX,
          y: panStart.y + deltaY
        });
      }
    }
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
    setHasDragged(false);
    // Reset drag start position
    setDragStart(null);
    setPanStart(null);
  };

  const handleTouchStart = (e) => {
    if (deploying || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const target = e.target;
    const isCountry = target.tagName === 'path' || target.tagName === 'polygon' || target.tagName === 'circle';
    touchDragStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchPanStartRef.current = { x: panRef.current.x, y: panRef.current.y };
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setPanStart({ x: panRef.current.x, y: panRef.current.y });
    setHasDragged(false);
    if (!isCountry) {
      setIsDragging(true);
    }
  };

  const handleTouchMove = React.useCallback((e) => {
    if (deploying || e.touches.length !== 1) return;
    const dragStartVal = touchDragStartRef.current;
    const panStartVal = touchPanStartRef.current;
    if (dragStartVal == null || panStartVal == null) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStartVal.x;
    const deltaY = touch.clientY - dragStartVal.y;
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      e.preventDefault();
      setHasDragged(true);
      setIsDragging(true);
      setPan({
        x: panStartVal.x + deltaX,
        y: panStartVal.y + deltaY
      });
    }
  }, [deploying]);

  const handleTouchEnd = () => {
    touchDragStartRef.current = null;
    touchPanStartRef.current = null;
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 0);
    setDragStart(null);
    setPanStart(null);
  };

  // Register wheel/touchmove in CAPTURE phase when the map container exists.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const opts = { passive: false, capture: true };
    container.addEventListener('wheel', handleWheel, opts);
    container.addEventListener('touchmove', handleTouchMove, opts);
    return () => {
      container.removeEventListener('wheel', handleWheel, opts);
      container.removeEventListener('touchmove', handleTouchMove, opts);
    };
  }, [handleWheel, handleTouchMove, svgPaths.length]);

  // When parent increments resetViewTrigger (e.g. world button), center the map
  useEffect(() => {
    if (resetViewTrigger !== 0) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  }, [resetViewTrigger]);

  // Handle double click to reset zoom and pan
  const handleDoubleClick = (e) => {
    // Don't reset if clicking on a country
    if (e.target.tagName === 'path' || e.target.tagName === 'polygon' || e.target.tagName === 'circle') {
      return;
    }
    e.preventDefault();
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Prevent context menu on right click
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  // Helper function to convert hex/rgba to RGB values
  const parseColor = (color) => {
    if (!color) return { r: 128, g: 128, b: 128 };
    
    // Handle rgba format
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
      }
    }
    
    // Handle hex format
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b };
    }
    
    return { r: 128, g: 128, b: 128 };
  };

  // Helper function to lighten/darken a color
  const adjustColor = (color, factor) => {
    const { r, g, b } = parseColor(color);
    const newR = Math.max(0, Math.min(255, Math.round(r * factor)));
    const newG = Math.max(0, Math.min(255, Math.round(g * factor)));
    const newB = Math.max(0, Math.min(255, Math.round(b * factor)));
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  // Helper function to get a darker variant of the faction color for stripes
  const getDarkerStripeColor = (baseColor) => {
    const { r, g, b } = parseColor(baseColor);
    // Create a darker version (reduce brightness by 30-40%)
    const factor = 0.65; // 35% darker
    const newR = Math.max(0, Math.min(255, Math.round(r * factor)));
    const newG = Math.max(0, Math.min(255, Math.round(g * factor)));
    const newB = Math.max(0, Math.min(255, Math.round(b * factor)));
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const factionsSet = React.useMemo(() => {
    if (factionsInGame == null) return null;
    if (factionsInGame instanceof Set) return factionsInGame;
    return new Set(Array.isArray(factionsInGame) ? factionsInGame : []);
  }, [factionsInGame]);

  // Memoize renderPath to ensure it updates when cellOwnersMap changes
  const renderPath = React.useCallback((pathData) => {
    const { cellId, countryName, type, id } = pathData;
    
    // Render all countries - mapped ones are clickable, unmapped ones are gray
    const isMapped = !!cellId;
    
    // Convert cellId to string for consistent lookup (JavaScript object keys are strings)
    const cellIdKey = cellId ? String(cellId) : null;
    const ownerFaction = isMapped ? (cellOwnersMap[cellIdKey] || null) : null;

    const ownerInGame = factionsSet === null || (ownerFaction != null && factionsSet.has(ownerFaction));
    
    // Calculate background color (and optional paint image for in-game owners)
    let background;
    let fillImagePatternId = null;
    if (!isMapped) {
      background = '#808080'; // Gray for unmapped countries
    } else if (!ownerFaction) {
      background = 'rgba(0, 0, 0, 0.1)'; // Very dark/transparent for unclaimed
    } else if (!ownerInGame) {
      background = DARK_GRAY_NOT_IN_GAME;
      fillImagePatternId = UNUSED_FACTION_PAINT_PATTERN_ID;
    } else if (!getFactionBackground) {
      background = 'rgba(0, 0, 0, 0.1)'; // Fallback if function not provided
    } else {
      const parsed = parseFactionBackgroundResult(getFactionBackground(ownerFaction), ownerFaction);
      background = parsed.color;
      if (parsed.image) {
        fillImagePatternId = factionImagePatternId(parsed.image);
      }
    }
    
    // Convert cellId to number for beaconCells array comparison (beaconCells contains numbers)
    const hasBeacon = isMapped && beaconCells.includes(Number(cellId));
    
    // Mission-specific indicators
    const isHighlighted = isMapped && highlightedCells.includes(Number(cellId));
    const isFortified = isMapped && fortifiedCells.includes(Number(cellId)); // Fortified by action (fortify country)
    const isCommandCenterFortified = isMapped && commandCenterFortifiedCells.includes(Number(cellId));
    const isOpponentFortified = isMapped && opponentFortifiedCells.includes(Number(cellId)); // Opponent action fortification
    const isOpponentCommandCenterFortified = isMapped && opponentCommandCenterFortifiedCells.includes(Number(cellId)); // Opponent command center only
    const isSelected = isMapped && selectedCell === cellId;
    const isMissionTarget = isMapped && missionTargetCell === cellId;
    const isMissionSubmissionHover = isMapped && missionSubmissionHoverCellId != null && Number(cellId) === Number(missionSubmissionHoverCellId);
    const isDevModeHighlight = isMapped && devModeHighlightCells.includes(Number(cellId));
    const isUnderAttack = isMapped && Array.isArray(cellsUnderAttack) && cellsUnderAttack.includes(Number(cellId));
    const isNewTradeOffer = isMapped && Array.isArray(newTradeOfferCells) && newTradeOfferCells.includes(Number(cellId));
    const isTectonicBoredrive = isMapped && Array.isArray(tectonicBoredriveCells) && tectonicBoredriveCells.includes(Number(cellId));
    const neutralFortCount = isMapped && neutralFortificationCountMap ? (neutralFortificationCountMap[Number(cellId)] || neutralFortificationCountMap[cellIdKey]) : 0;
    const useFortifiedVisual = isFortified || isCommandCenterFortified || isOpponentFortified || isOpponentCommandCenterFortified;
    
    // For fortified countries: prefer action (fortify country) stripe over command center when both exist
    let fillValue = fillImagePatternId ? `url(#${fillImagePatternId})` : background;
    let stripePatternId = null;
    if (!ownerInGame) {
      if (neutralFortCount >= 1 && neutralFortCount <= 3) {
        fillValue = `url(#${neutralFortPaintPatternId(neutralFortCount)})`;
      } else {
        fillValue = `url(#${UNUSED_FACTION_PAINT_PATTERN_ID})`;
      }
    } else if (isFortified) {
      // Keep faction paint fill; grey stripe overlay (same treatment as Command Center)
      stripePatternId = ACTION_FORTIFY_STRIPE_PATTERN_ID;
    } else if (isCommandCenterFortified) {
      // Keep faction paint fill; darker purpose stripe is an overlay
      stripePatternId = 'stripe-pattern-command-center';
    } else if (isOpponentFortified) {
      // Keep faction paint fill; grey stripe overlay
      stripePatternId = ACTION_FORTIFY_STRIPE_PATTERN_ID;
    } else if (isOpponentCommandCenterFortified) {
      // Keep faction paint fill; darker purpose stripe is an overlay
      const cellColors = opponentCommandCenterStripeByCellId && opponentCommandCenterStripeByCellId[Number(cellId)];
      stripePatternId = cellColors && cellColors.base
        ? opponentCcPatternIdFromColors(cellColors.base, cellColors.darker)
        : 'stripe-pattern-opponent-command-center';
    }

    // Determine stroke color based on mission indicators (all countries have glowing white border by default)
    let strokeColor = 'rgba(255, 255, 255, 0.95)';
    let strokeWidth = '1.5';
    let strokeDasharray = null;
    
    if (isUnderAttack) {
      strokeColor = 'rgba(220, 20, 60, 0.7)'; // Subtle crimson stroke (pulse overlay does the main effect)
      strokeWidth = '2';
    } else if (isNewTradeOffer) {
      strokeColor = 'rgba(255, 215, 0, 0.95)';
      strokeWidth = '2.5';
    } else if (isDevModeHighlight) {
      strokeColor = 'rgba(255, 215, 0, 0.9)'; // Golden for dev mode command center eligible
      strokeWidth = '3';
      strokeDasharray = '6,3'; // Dashed line to distinguish from regular highlight
    } else if (isMissionTarget) {
      strokeColor = 'rgba(255, 215, 0, 0.9)'; // Gold for mission target
      strokeWidth = '3';
    } else if (isMissionSubmissionHover) {
      strokeColor = 'rgba(255, 215, 0, 0.85)'; // Gold for mission submission hover (e.g. "Conquer Seychelles")
      strokeWidth = '2.5';
    } else if (useFortifiedVisual || neutralFortCount > 0) {
      // Fortified, opponent-fortified, or neutral-fortified: glowing white border (stripe is the indicator)
      strokeColor = 'rgba(255, 255, 255, 0.95)';
      strokeWidth = '1.5';
    } else if (isSelected) {
      strokeColor = 'rgba(74, 158, 255, 0.9)'; // Blue for selected
      strokeWidth = '2.5';
    } else if (isHighlighted) {
      strokeColor = 'rgba(74, 158, 255, 0.6)'; // Light blue for highlighted
      strokeWidth = '2';
    } else if (!isMapped) {
      strokeColor = 'rgba(255, 255, 255, 0.85)';
      strokeWidth = '1.5';
    }
    
    const pathFillValue = fillValue;

    const stateFilter = isUnderAttack || isNewTradeOffer
      ? 'none' // Overlay pulse replaces filter
      : (isMissionSubmissionHover || isMissionTarget || isDevModeHighlight
          ? 'brightness(1.4)'
          : 'none');

    const combinedFilter = stateFilter;
    const commonProps = {
      id,
      fill: pathFillValue,
      stroke: strokeColor,
      strokeWidth: strokeWidth,
      strokeDasharray: strokeDasharray,
      opacity: deploying ? 0.5 : 1,
      style: {
        cursor: isMapped && !deploying ? (isDragging ? 'grabbing' : 'pointer') : 'not-allowed',
        filter: combinedFilter !== 'none' ? combinedFilter : undefined,
        pointerEvents: hasDragged ? 'none' : 'fill'
      },
      onClick: isMapped ? (e) => handleCountryClick(cellId, e) : undefined,
      onMouseEnter: isMapped ? (e) => handleCountryMouseEnter(e, cellId) : undefined,
      onMouseLeave: isMapped ? handleCountryMouseLeave : undefined,
      'data-cell-id': cellId || null,
      'data-country-name': countryName,
      'data-mapped': isMapped ? 'true' : 'false',
      'data-fortified': isFortified ? 'true' : 'false',
      'data-mission-target': isMissionTarget ? 'true' : 'false',
      'data-dev-highlight': isDevModeHighlight ? 'true' : 'false'
    };
    
    // Tectonic Boredrive: visible rupture overlay (solid tint so it always shows; image pattern can fail to load)
    const tectonicRuptureOverlayProps = isTectonicBoredrive
      ? {
          id: `${id}-tectonic-rupture-overlay`,
          fill: 'rgba(139, 69, 19, 0.55)',
          stroke: 'none',
          strokeWidth: 0,
          strokeDasharray: null,
          opacity: 1,
          style: {
            pointerEvents: 'none'
          },
          onClick: undefined,
          onMouseEnter: undefined,
          onMouseLeave: undefined,
          'data-cell-id': null,
          'data-country-name': null,
          'data-mapped': null,
          'data-fortified': null,
          'data-mission-target': null,
          'data-dev-highlight': null
        }
      : null;

    // Under-attack: pulsating red overlay so original color shows through when pulse ebbs
    const underAttackOverlayProps = isUnderAttack
      ? {
          id: `${id}-under-attack-overlay`,
          fill: 'rgba(220, 20, 60, 0.7)',
          stroke: 'none',
          strokeWidth: 0,
          strokeDasharray: null,
          opacity: 1,
          style: {
            pointerEvents: 'none'
          },
          onClick: undefined,
          onMouseEnter: undefined,
          onMouseLeave: undefined,
          className: 'worldmap-under-attack-overlay',
          'data-cell-id': null,
          'data-country-name': null,
          'data-mapped': null,
          'data-fortified': null,
          'data-mission-target': null,
          'data-dev-highlight': null
        }
      : null;

    const newTradeOfferOverlayProps = isNewTradeOffer && !isUnderAttack
      ? {
          id: `${id}-new-trade-offer-overlay`,
          fill: 'rgba(255, 215, 0, 0.7)',
          stroke: 'none',
          strokeWidth: 0,
          strokeDasharray: null,
          opacity: 1,
          style: {
            pointerEvents: 'none'
          },
          onClick: undefined,
          onMouseEnter: undefined,
          onMouseLeave: undefined,
          className: 'worldmap-new-trade-offer-overlay',
          'data-cell-id': null,
          'data-country-name': null,
          'data-mapped': null,
          'data-fortified': null,
          'data-mission-target': null,
          'data-dev-highlight': null
        }
      : null;

    const showFortifiedStripeOverlay = ownerInGame && stripePatternId && (
      isFortified || isCommandCenterFortified || isOpponentFortified || isOpponentCommandCenterFortified
    );
    const commandCenterStripeOverlayProps = showFortifiedStripeOverlay
      ? {
          id: `${id}-command-center-stripe-overlay`,
          fill: `url(#${stripePatternId})`,
          stroke: 'none',
          strokeWidth: 0,
          strokeDasharray: null,
          opacity: 1,
          style: {
            pointerEvents: 'none'
          },
          onClick: undefined,
          onMouseEnter: undefined,
          onMouseLeave: undefined,
          'data-cell-id': null,
          'data-country-name': null,
          'data-mapped': null,
          'data-fortified': null,
          'data-mission-target': null,
          'data-dev-highlight': null
        }
      : null;

    return (
      <g key={`${id}-${ownerFaction || 'unclaimed'}-${stripePatternId || 'none'}`}>
        <CountryPath
          pathData={pathData}
          commonProps={commonProps}
          hasBeacon={hasBeacon}
          type={type}
        />
        {commandCenterStripeOverlayProps && (
          <CountryPath
            pathData={pathData}
            commonProps={commandCenterStripeOverlayProps}
            hasBeacon={false}
            type={type}
          />
        )}
        {tectonicRuptureOverlayProps && (
          <CountryPath
            pathData={pathData}
            commonProps={tectonicRuptureOverlayProps}
            hasBeacon={false}
            type={type}
          />
        )}
        {underAttackOverlayProps && (
          <CountryPath
            pathData={pathData}
            commonProps={underAttackOverlayProps}
            hasBeacon={false}
            type={type}
          />
        )}
        {newTradeOfferOverlayProps && (
          <CountryPath
            pathData={pathData}
            commonProps={newTradeOfferOverlayProps}
            hasBeacon={false}
            type={type}
          />
        )}
      </g>
    );
  }, [cellOwnersMap, getFactionBackground, factionsSet, beaconCells, deploying, isDragging, hasDragged, handleCountryClick, handleCountryMouseEnter, handleCountryMouseLeave, highlightedCells, fortifiedCells, commandCenterFortifiedCells, selectedCell, missionTargetCell, missionSubmissionHoverCellId, opponentFortifiedCells, opponentCommandCenterFortifiedCells, opponentCommandCenterStripeByCellId, devModeHighlightCells, neutralFortificationCountMap, cellsUnderAttack, newTradeOfferCells, tectonicBoredriveCells]);

  // Darker-only stripe overlay for Command Center fortified countries (faction paint shows through the gaps)
  const commandCenterStripePattern = React.useMemo(() => {
    if (!commandCenterFortifiedCells || commandCenterFortifiedCells.length === 0) return null;
    const baseColor = viewerCommandCenterStripeBaseColor
      ? (() => {
          const { r, g, b } = parseColor(viewerCommandCenterStripeBaseColor);
          return `rgb(${r}, ${g}, ${b})`;
        })()
      : 'rgb(45, 27, 78)';
    const darkerColor = viewerCommandCenterStripeDarkerColor
      ? (() => {
          const { r, g, b } = parseColor(viewerCommandCenterStripeDarkerColor);
          return `rgb(${r}, ${g}, ${b})`;
        })()
      : getDarkerStripeColor(baseColor);
    return createDarkerStripeOverlayPattern('stripe-pattern-command-center', darkerColor);
  }, [commandCenterFortifiedCells, viewerCommandCenterStripeBaseColor, viewerCommandCenterStripeDarkerColor]);

  // Darker-only stripe overlay for opponent Command Center (fallback when per-cell not available)
  const opponentCommandCenterStripePattern = React.useMemo(() => {
    if (!opponentCommandCenterFortifiedCells || opponentCommandCenterFortifiedCells.length === 0) return null;
    const baseColor = opponentCommandCenterStripeBaseColor
      ? (() => {
          const { r, g, b } = parseColor(opponentCommandCenterStripeBaseColor);
          return `rgb(${r}, ${g}, ${b})`;
        })()
      : 'rgb(80, 50, 100)';
    const darkerColor = opponentCommandCenterStripeDarkerColor
      ? (() => {
          const { r, g, b } = parseColor(opponentCommandCenterStripeDarkerColor);
          return `rgb(${r}, ${g}, ${b})`;
        })()
      : getDarkerStripeColor(baseColor);
    return createDarkerStripeOverlayPattern('stripe-pattern-opponent-command-center', darkerColor);
  }, [opponentCommandCenterFortifiedCells, opponentCommandCenterStripeBaseColor, opponentCommandCenterStripeDarkerColor]);

  // Per-cell opponent Command Center stripe patterns (FFA/2v2: color by cell owner purpose so e.g. Survive = green)
  const opponentCommandCenterStripePatternsByCell = React.useMemo(() => {
    if (!opponentCommandCenterStripeByCellId || typeof opponentCommandCenterStripeByCellId !== 'object') return [];
    const seen = new Set();
    const patterns = [];
    for (const colors of Object.values(opponentCommandCenterStripeByCellId)) {
      if (!colors || !colors.base) continue;
      const key = `${colors.base}-${colors.darker || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const darkerColor = colors.darker
        ? (() => {
            const { r, g, b } = parseColor(colors.darker);
            return `rgb(${r}, ${g}, ${b})`;
          })()
        : getDarkerStripeColor(colors.base);
      const patternId = opponentCcPatternIdFromColors(colors.base, colors.darker);
      patterns.push(createDarkerStripeOverlayPattern(patternId, darkerColor));
    }
    return patterns;
  }, [opponentCommandCenterStripeByCellId]);

  const factionImagePatterns = React.useMemo(() => {
    if (!getFactionBackground) return [];
    const seenImages = new Set();
    const patterns = [];
    Object.values(cellOwnersMap).forEach((faction) => {
      if (!faction) return;
      const parsed = parseFactionBackgroundResult(getFactionBackground(faction), faction);
      if (!parsed.image || seenImages.has(parsed.image)) return;
      seenImages.add(parsed.image);
      const patternId = factionImagePatternId(parsed.image);
      patterns.push(createFactionImagePattern(patternId, parsed.image));
    });
    return patterns;
  }, [cellOwnersMap, getFactionBackground]);

  if (svgPaths.length === 0) {
    return (
      <div 
        className="world-map-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <div>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>World Map Loading...</p>
          <p style={{ fontSize: '12px', color: '#aaa', marginBottom: '5px' }}>
            To use an interactive world map:
          </p>
          <p style={{ fontSize: '11px', color: '#888' }}>
            1. Place a world-map.svg file in /public/images/
          </p>
          <p style={{ fontSize: '11px', color: '#888' }}>
            2. Ensure country paths have id attributes matching country names
          </p>
          <p style={{ fontSize: '11px', color: '#888' }}>
            3. Supported formats: path, polygon, circle elements
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="world-map-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
        pointerEvents: interactive ? 'auto' : 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        overflow: 'hidden'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      <svg
        ref={svgRef}
        className="worldmap-paint-svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox={svgViewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent'
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {UNUSED_FACTION_PAINT_PATTERN}
          {ACTION_FORTIFY_STRIPE_PATTERN}
          {NEUTRAL_FORT_PAINT_PATTERNS}
          {factionImagePatterns}
          {commandCenterStripePattern}
          {opponentCommandCenterStripePattern}
          {opponentCommandCenterStripePatternsByCell}
        </defs>
        <g
          transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}
          style={{ transformOrigin: '0 0', pointerEvents: hasDragged ? 'none' : 'auto' }}
        >
          {svgPaths.map(renderPath)}
          {/* Attack arrow: mutated on hover via refs so the map does not re-render */}
          <g ref={arrowGroupRef} pointerEvents="none">
            <line
              ref={arrowGlowRef}
              x1="0"
              y1="0"
              x2="0"
              y2="0"
              stroke="rgba(255, 100, 0, 0.6)"
              strokeWidth={6}
              strokeLinecap="round"
            />
            <line
              ref={arrowShaftRef}
              x1="0"
              y1="0"
              x2="0"
              y2="0"
              stroke="rgba(255, 215, 0, 0.9)"
              strokeWidth={3}
              strokeLinecap="round"
            />
            <polygon
              ref={arrowHeadRef}
              points="0,0 0,0 0,0"
              fill="rgba(255, 215, 0, 0.95)"
              stroke="rgba(255, 180, 0, 0.9)"
              strokeWidth={1.5}
            />
          </g>
          {/* Hint popup with arrow pointing to cell (e.g. Madagascar trade) */}
          {hintPopup && hintPopup.message && hintTargetCoords && (
            <g pointerEvents="none">
              <rect
                x={hintTargetCoords.cx - 340}
                y={hintTargetCoords.cy - 26}
                width={320}
                height={52}
                rx={8}
                fill="rgba(0, 0, 0, 0.88)"
                stroke="rgba(255, 215, 0, 0.9)"
                strokeWidth={2}
              />
              <text
                x={hintTargetCoords.cx - 180}
                y={hintTargetCoords.cy}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize={14}
                fontFamily="Arial, sans-serif"
                fontWeight="bold"
              >
                {hintPopup.message}
              </text>
              <line
                x1={hintTargetCoords.cx - 20}
                y1={hintTargetCoords.cy}
                x2={hintTargetCoords.cx}
                y2={hintTargetCoords.cy}
                stroke="rgba(255, 215, 0, 0.95)"
                strokeWidth={3}
                strokeLinecap="round"
              />
              <polygon
                points={`${hintTargetCoords.cx},${hintTargetCoords.cy} ${hintTargetCoords.cx - 14},${hintTargetCoords.cy - 8} ${hintTargetCoords.cx - 14},${hintTargetCoords.cy + 8}`}
                fill="rgba(255, 215, 0, 0.95)"
                stroke="rgba(255, 180, 0, 0.9)"
                strokeWidth={1.5}
              />
            </g>
          )}
        </g>
      </svg>
      <svg
        className="worldmap-hover-overlay-svg"
        viewBox={svgViewBox}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`} style={{ transformOrigin: '0 0' }}>
          <g ref={hoverOutlineGroupRef} pointerEvents="none" />
        </g>
      </svg>
    </div>
  );
};

export default React.memo(WorldMap);
